import React from "react";
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { dateStrToObj, dateObjToShortStr } from "../../requests/utilities/helpers";
import { pivotEntityTotals } from "./entityPivot";
import {
  formatAxisNumber,
  formatQhClock,
  formatQhTick,
  qhHourTicks,
} from "./chartFormat";

function formatNumber(value, format, axis, compact) {
  if (axis) return formatAxisNumber(value, format, compact);

  switch (format) {
    case "dollars":
      return `$${parseFloat(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`; // e.g. $1,234.45
    case "percentage":
      return `${(value * 100).toFixed(2)}%`;
    case "number":
      return `${parseFloat(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    default:
      return value.toFixed(2);
  }
}

function getMetricLabel(metricValue, metrics) {
  if (!metricValue || !metrics || !Array.isArray(metrics)) {
    return metricValue || "Unknown";
  }

  let baseValue = metricValue;
  let suffix = "";

  if (metricValue.endsWith("_previous_year")) {
    baseValue = metricValue.replace("_previous_year", "");
    suffix = " (Prev Year)";
  }

  const metric = metrics.find((m) => m && m.value === baseValue);
  if (metric && metric.label) {
    return `${metric.label}${suffix}`;
  }
  return metricValue;
}

const colors = [
  "#8884d8", // Purple
  "#82ca9d", // Green
  "#ffc658", // Yellow
  "#ff7300", // Orange
  "#d0ed57", // Light Green
  "#a4de6c", // Lighter Green
  "#ff69b4", // Pink
  "#8dd1e1", // Light Blue
  "#83a6ed", // Lighter Blue
  "#888888", // Gray
];

const BarChart = ({
  activeTab,
  intradayData,
  data,
  rawData,
  selectedMetrics,
  metrics,
  compareLocations,
  compareDow,
  isDashboard,
  chartHeight,
  compactAxis,
  locationData,
  layout,
}) => {
  const yAxisWidth = compactAxis ? 40 : 60;
  const chartMargin = compactAxis
    ? { top: 20, right: 15, left: 0, bottom: 5 }
    : { top: 20, right: 30, left: 20, bottom: 5 };
  const safeMetrics = metrics || [];
  const safeSelectedMetrics = selectedMetrics || [];
  const safeData = data || [];
  const safeIntradayData = intradayData || [];
  const safeRawData = rawData || [];

  // Helper to format date label based on activeTab (Daily vs Weekly)
  const formatDateLabel = (dateStr) => {
    if (typeof dateStr !== "string") {
      return String(dateStr);
    }
    const dt = dateStrToObj(dateStr);
    const formattedDate = dateObjToShortStr(dt);
    return activeTab === "Weekly" ? `Week of ${formattedDate}` : formattedDate;
  };

  const firstMetric = safeMetrics.find((m) => m && m.value === safeSelectedMetrics[0]);
  const formatType = firstMetric && firstMetric.format ? firstMetric.format : "number";
  const baseHeight = chartHeight ?? (isDashboard ? 300 : 400);

  // by_entity: one group per store/employee/role, one bar per metric, from
  // each entity's totals. Many entities switch to horizontal bars that
  // scroll so every name stays readable.
  const renderEntityBars = (rows) => {
    const horizontal = rows.length >= 8;
    const contentHeight = horizontal
      ? Math.max(baseHeight, rows.length * (safeSelectedMetrics.length * 12 + 16) + 70)
      : baseHeight;
    const valueTick = (val) => formatNumber(val, formatType, true, compactAxis);
    // Shorten from the middle so names that share a prefix ("The Victor
    // Tavern - Eastlake" / "- Edmonds") stay distinct; the tooltip has the
    // full name.
    const maxChars = compactAxis ? 24 : 30;
    const shortName = (name) => {
      if (typeof name !== "string" || name.length <= maxChars) return name;
      const keep = maxChars - 1;
      const head = Math.ceil(keep / 2);
      return `${name.slice(0, head).trimEnd()}…${name.slice(name.length - (keep - head)).trimStart()}`;
    };
    const longestName = Math.max(...rows.map((row) => String(shortName(row.entity)).length));
    const nameAxisWidth = Math.min(maxChars * 7 + 12, Math.max(60, longestName * 7 + 12));
    // Single-line tick: Recharts would otherwise wrap long names.
    const NameTick = ({ x, y, payload }) => (
      <text x={x} y={y} dy={4} textAnchor="end" fontSize={12} fill="#666">
        {shortName(payload.value)}
      </text>
    );
    const formatTooltip = (val, name) => {
      const info = safeMetrics.find((m) => m && m.value === name);
      const shown = val === null || val === undefined || isNaN(val)
        ? "—"
        : formatNumber(val, (info && info.format) || formatType, false);
      return [shown, getMetricLabel(name, safeMetrics)];
    };

    return (
      <div style={{ height: baseHeight, overflowY: contentHeight > baseHeight ? "auto" : "hidden" }}>
        <ResponsiveContainer width="100%" height={contentHeight}>
          <RechartsBarChart
            data={rows}
            layout={horizontal ? "vertical" : "horizontal"}
            margin={chartMargin}
          >
            <CartesianGrid strokeDasharray="3 3" />
            {horizontal ? (
              <XAxis type="number" tickFormatter={valueTick} />
            ) : (
              <XAxis dataKey="entity" tickFormatter={shortName} interval={0} />
            )}
            {horizontal ? (
              <YAxis
                type="category"
                dataKey="entity"
                width={nameAxisWidth}
                interval={0}
                tick={<NameTick />}
              />
            ) : (
              <YAxis width={yAxisWidth} tickFormatter={valueTick} />
            )}
            <Tooltip formatter={formatTooltip} />
            <Legend
              layout="horizontal"
              align="center"
              verticalAlign="top"
              formatter={(val) => getMetricLabel(val, safeMetrics)}
              wrapperStyle={{ paddingBottom: "15px" }}
            />
            {safeSelectedMetrics.map((metric, index) => (
              <Bar
                key={metric}
                dataKey={metric}
                name={metric}
                fill={colors[index % colors.length]}
              />
            ))}
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  if (layout === "by_entity") {
    const entityRows = pivotEntityTotals(locationData, safeSelectedMetrics);
    if (entityRows.length > 0) return renderEntityBars(entityRows);
  }

  const renderChart = () => {
    if (activeTab === "Intraday" && safeIntradayData?.length > 0) {
      if (compareDow) {
        const groupedByQh = safeIntradayData.reduce((acc, row) => {
          if (!row || !row.qh_num || !row.day_of_week) return acc;
          const qh = row.qh_num;
          if (!acc[qh]) acc[qh] = { qh_num: qh };
          if (safeSelectedMetrics[0]) {
            acc[qh][row.day_of_week] = row[safeSelectedMetrics[0]];
          }
          return acc;
        }, {});

        const processedDow = Object.values(groupedByQh)
          .sort((a, b) => a.qh_num - b.qh_num);

        const dayOrder = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
        let dowList = [...new Set(safeIntradayData.map((d) => d && d.day_of_week).filter(Boolean))];
        dowList = dowList.sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b));

        return (
          <RechartsBarChart
            width={500}
            height={300}
            data={processedDow}
            margin={chartMargin}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="qh_num"
              tickFormatter={formatQhTick}
              ticks={qhHourTicks(processedDow, compactAxis ? 7 : 13)}
            />
            <YAxis width={yAxisWidth} tickFormatter={(val) => formatNumber(val, formatType, true, compactAxis)} />
            <Tooltip
              formatter={(val, name) => [formatNumber(val, formatType, false), name]}
              labelFormatter={(labelVal) => `Time: ${formatQhClock(labelVal)}`}
            />
            <Legend layout="horizontal" align="center" verticalAlign="top" wrapperStyle={{ paddingBottom: '15px' }} />
            {dowList.map((day, index) => (
              <Bar
                key={day}
                dataKey={day}
                name={day}
                fill={colors[index % colors.length]}
              />
            ))}
          </RechartsBarChart>
        );
      } else if (compareLocations) {
        const groupedIntraday = safeIntradayData.reduce((acc, row) => {
          const entityKey = row?.location || row?.employee || row?.role;
          if (!row || !row.qh_num || !entityKey) return acc;
          const qh = row.qh_num;
          if (!acc[qh]) acc[qh] = { qh_num: qh };
          if (safeSelectedMetrics[0]) {
            acc[qh][entityKey] = row[safeSelectedMetrics[0]];
          }
          return acc;
        }, {});

        const processedIntraday = Object.values(groupedIntraday)
          .sort((a, b) => a.qh_num - b.qh_num);

        const intradayLocations = [...new Set(safeIntradayData.map((d) => d && (d.location || d.employee || d.role)).filter(Boolean))];

        return (
          <RechartsBarChart
            width={500}
            height={300}
            data={processedIntraday}
            margin={chartMargin}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="qh_num"
              tickFormatter={formatQhTick}
              ticks={qhHourTicks(processedIntraday, compactAxis ? 7 : 13)}
            />
            <YAxis width={yAxisWidth} tickFormatter={(val) => formatNumber(val, formatType, true, compactAxis)} />
            <Tooltip
              formatter={(val, name) => [formatNumber(val, formatType, false), name]}
              labelFormatter={(labelVal) => `Time: ${formatQhClock(labelVal)}`}
            />
            <Legend layout="horizontal" align="center" verticalAlign="top" wrapperStyle={{ paddingBottom: '15px' }} />
            {intradayLocations.map((loc, index) => (
              <Bar
                key={loc}
                dataKey={loc}
                name={loc}
                fill={colors[index % colors.length]}
              />
            ))}
          </RechartsBarChart>
        );
      } else {
        return (
          <RechartsBarChart
            width={500}
            height={300}
            data={safeIntradayData}
            margin={chartMargin}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="qh_num"
              tickFormatter={formatQhTick}
              ticks={qhHourTicks(safeIntradayData, compactAxis ? 7 : 13)}
            />
            <YAxis width={yAxisWidth} tickFormatter={(val) => formatNumber(val, formatType, true, compactAxis)} />
            <Tooltip
              formatter={(val, name) => [formatNumber(val, formatType, false), getMetricLabel(name, safeMetrics)]}
              labelFormatter={(labelVal) => `Time: ${formatQhClock(labelVal)}`}
            />
            <Legend
            layout="horizontal"
            align="center"
            verticalAlign="top"
            formatter={(val) => getMetricLabel(val, safeMetrics)}
            wrapperStyle={{ paddingBottom: '15px' }}
          />
            {safeSelectedMetrics.map((metric, index) => (
              <Bar
                key={metric}
                dataKey={metric}
                name={getMetricLabel(metric, safeMetrics)}
                fill={colors[index % colors.length]}
              />
            ))}
          </RechartsBarChart>
        );
      }
    }

    // Daily data
    if (!compareLocations) {
      return (
        <RechartsBarChart
          width={500}
          height={300}
          data={safeData}
          margin={chartMargin}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tickFormatter={(val) => {
              if (typeof val !== "string") return "";
              const dt = dateStrToObj(val);
              return `${dt.getMonth() + 1}/${dt.getDate()}`;
            }}
          />
          <YAxis width={yAxisWidth} tickFormatter={(value) => formatNumber(value, formatType, true, compactAxis)} />
          <Tooltip
            formatter={(val, name) => [formatNumber(val, formatType, false), getMetricLabel(name, safeMetrics)]}
            labelFormatter={formatDateLabel}
          />
          <Legend
            layout="horizontal"
            align="center"
            verticalAlign="top"
            formatter={(val) => getMetricLabel(val, safeMetrics)}
            wrapperStyle={{ paddingBottom: '15px' }}
          />
          {safeSelectedMetrics.map((metric, index) => (
            <Bar
              key={metric}
              dataKey={metric}
              name={getMetricLabel(metric, safeMetrics)}
              fill={colors[index % colors.length]}
            />
          ))}
        </RechartsBarChart>
      );
    } else {
      // Compare locations/employees for daily
      const groupedData = safeRawData.reduce((acc, curr) => {
        if (!curr || !curr.date) return acc;
        const date = curr.date;
        if (!acc[date]) acc[date] = { date };
        const entityKey = curr.location || curr.employee || curr.role;
        if (entityKey && safeSelectedMetrics[0]) {
          acc[date][entityKey] = curr[safeSelectedMetrics[0]];
        }
        return acc;
      }, {});
      const processedData = Object.values(groupedData);
      const locations = [...new Set(safeRawData.map((d) => d && (d.location || d.employee || d.role)).filter(Boolean))];

      return (
        <RechartsBarChart
          width={500}
          height={300}
          data={processedData}
          margin={chartMargin}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tickFormatter={(val) => {
              if (typeof val !== "string") return "";
              const dt = dateStrToObj(val);
              return `${dt.getMonth() + 1}/${dt.getDate()}`;
            }}
          />
          <YAxis width={yAxisWidth} tickFormatter={(val) => formatNumber(val, formatType, true, compactAxis)} />
          <Tooltip
            formatter={(val, name) => [formatNumber(val, formatType, false), name]}
            labelFormatter={(str) => {
              if (typeof str !== "string") return String(str);
              const dt = dateStrToObj(str);
              const metricLabel = firstMetric?.label || 'Metric';
              const formattedDate = dateObjToShortStr(dt);
              const dateLabel = activeTab === "Weekly" ? `Week of ${formattedDate}` : formattedDate;
              return `${metricLabel} on ${dateLabel}`;
            }}
          />
          <Legend layout="horizontal" align="center" verticalAlign="top" wrapperStyle={{ paddingBottom: '15px' }} />
          {locations.map((location, index) => (
            <Bar
              key={location}
              dataKey={location}
              name={location}
              fill={colors[index % colors.length]}
            />
          ))}
        </RechartsBarChart>
      );
    }
  };

  return (
    <ResponsiveContainer width="100%" height={baseHeight}>
      {renderChart()}
    </ResponsiveContainer>
  );
};

export default BarChart;
